import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionHistoryTable = ({ transactions }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const itemsPerPage = 10;

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-error bg-red-50';
    if (score >= 60) return 'text-warning bg-amber-50';
    if (score >= 40) return 'text-accent bg-yellow-50';
    return 'text-success bg-green-50';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTransactions = [...transactions]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'timestamp') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const paginatedTransactions = sortedTransactions?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(transactions?.length / itemsPerPage);

  return (
    <div className="bg-card border border-border rounded-lg shadow-forensic-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="History" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-text-primary">Transaction History</h3>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Filter" size={16} color="currentColor" className="mr-2" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} color="currentColor" className="mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">
                <button 
                  onClick={() => handleSort('timestamp')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Timestamp</span>
                  <Icon name="ArrowUpDown" size={14} color="currentColor" />
                </button>
              </th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Type</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">
                <button 
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Amount</span>
                  <Icon name="ArrowUpDown" size={14} color="currentColor" />
                </button>
              </th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Counterparty</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Risk Score</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Status</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions?.map((transaction, index) => (
              <tr key={index} className="border-b border-border hover:bg-muted/50">
                <td className="p-4">
                  <div className="text-sm text-text-primary">{transaction?.date}</div>
                  <div className="text-xs text-text-secondary">{transaction?.time}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={transaction?.type === 'incoming' ? 'ArrowDownLeft' : 'ArrowUpRight'} 
                      size={16} 
                      color={transaction?.type === 'incoming' ? 'var(--color-success)' : 'var(--color-warning)'} 
                    />
                    <span className="text-sm text-text-primary capitalize">{transaction?.type}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-text-primary">{transaction?.amount} BTC</div>
                  <div className="text-xs text-text-secondary">≈ ${transaction?.usdValue?.toLocaleString()}</div>
                </td>
                <td className="p-4">
                  <div className="font-data text-xs text-text-secondary max-w-32 truncate">
                    {transaction?.counterparty}
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(transaction?.riskScore)}`}>
                    {transaction?.riskScore}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${
                      transaction?.confirmations >= 6 ? 'bg-success' : 
                      transaction?.confirmations >= 1 ? 'bg-warning' : 'bg-error'
                    }`} />
                    <span className="text-xs text-text-secondary">
                      {transaction?.confirmations} conf.
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <Button variant="ghost" size="sm">
                    <Icon name="ExternalLink" size={14} color="currentColor" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, transactions?.length)} of {transactions?.length} transactions
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <Icon name="ChevronLeft" size={16} color="currentColor" />
              </Button>
              <span className="text-sm text-text-primary px-3 py-1">
                {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <Icon name="ChevronRight" size={16} color="currentColor" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryTable;